import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { ModalConfig, NonPersistedModalName, PersistedModalName, Priority } from '../models/modal.model';
import * as nonPersistedModals from '../modals/non-persisted-modals';
import * as persistedModals from '../modals/persisted-modals';
import { ChallengeEventType, LimitedTimeEventType } from '../models/limitedTimeEvent.model';

@Injectable({
  providedIn: 'root',
})
export class ModalComponentManager {
  findComponentType(modal: ModalConfig): ComponentType<unknown> {
    switch (modal.modalType) {
      case PersistedModalName.Default:
        break;
      case PersistedModalName.Welcome:
        return persistedModals.WelcomeModalComponent;
      case PersistedModalName.Bonus:
        return persistedModals.BonusRewardModalComponent;
      case PersistedModalName.CampaignPersisted:
        return persistedModals.CampaignModalComponent;
      case PersistedModalName.Daily:
        return persistedModals.DailyBonusModalComponent;
      case PersistedModalName.StartNow:
        return persistedModals.StartNowOnboardingComponent;
      case PersistedModalName.PromoCode:
        return persistedModals.PromoCodeModalComponent;
      case PersistedModalName.PromoCodeRedeemed:
        return persistedModals.PromoCodeRedeemedModalComponent;
      case PersistedModalName.PromoCodeExpired:
        return persistedModals.PromoCodeExpiredModalComponent;
      case PersistedModalName.VideoAds:
        return persistedModals.VideoadsRewardModalComponent;
      case PersistedModalName.BuySuccess:
        return persistedModals.BuySuccessModalComponent;
      case PersistedModalName.NotifyDiscontinued:
        return persistedModals.NotifyDiscontinuedModalComponent;
      case PersistedModalName.DiscontinuedReward:
        return persistedModals.DiscontinuedRewardModalComponent;
      case PersistedModalName.Campaign:
        return persistedModals.CampaignModalComponent;
      case PersistedModalName.LimitedTimeEventStart:
        return this.manageLimitedTimeEventStart(modal);
      case PersistedModalName.LimitedTimeEventProgress:
        return this.manageLimitedTimeEventProgress(modal);
      case PersistedModalName.LimitedTimeEventRedeem:
        return this.manageLimitedTimeEventReward(modal);
      case PersistedModalName.OfferPlaceholder:
        return persistedModals.OfferModalComponent;
      case PersistedModalName.UpdateTos:
        return persistedModals.UpdateTosModalComponent;
      case PersistedModalName.RestoreStreak:
        return persistedModals.KeepStreakComponent;
      case PersistedModalName.RichClubBonus:
        return persistedModals.RichClubBonusComponent;
      case PersistedModalName.RichClubAvailable:
        return persistedModals.RichClubBonusAvailableComponent;
      case PersistedModalName.RollingBonus:
        return persistedModals.RollingOffersBonusComponent;
      default:
        break;
    }
  }

  setNonPersisted(modalName: NonPersistedModalName): ComponentType<unknown> {
    switch (modalName) {
      case NonPersistedModalName.ShopModalComponent:
        return nonPersistedModals.ShopModalComponent;
      case NonPersistedModalName.UserTrophiesModalComponent:
        return nonPersistedModals.UserTrophiesModalComponent;
      case NonPersistedModalName.SearchGameModalComponent:
        return nonPersistedModals.SearchGameModalComponent;
      case NonPersistedModalName.GameTrophiesModalComponent:
        return nonPersistedModals.GameTrophiesModalComponent;
      case NonPersistedModalName.InfoGameTrophiesModalComponent:
        return nonPersistedModals.InfoGameTrophiesModalComponent;
      case NonPersistedModalName.NotifyDiscontinuedModalComponent:
        return persistedModals.NotifyDiscontinuedModalComponent;
      case NonPersistedModalName.PublicProfileComponent:
        return nonPersistedModals.PublicProfileComponent;
      case NonPersistedModalName.ManagePasswordComponent:
        return nonPersistedModals.ManagePasswordComponent;
      case NonPersistedModalName.ManageProfileComponent:
        return nonPersistedModals.ManageProfileComponent;
      case NonPersistedModalName.ManageNameComponent:
        return nonPersistedModals.ManageNameComponent;
      case NonPersistedModalName.LoginErrorModalComponent:
        return nonPersistedModals.LoginErrorModalComponent;
      case NonPersistedModalName.PurchaseErrorModalComponent:
        return nonPersistedModals.PurchaseErrorModalComponent;
      case NonPersistedModalName.InfoGoldenBombModalComponent:
        return nonPersistedModals.InfoGoldenBombModalComponent;
      case NonPersistedModalName.InfoUserTrophiesModalComponent:
        return nonPersistedModals.InfoUserTrophiesModalComponent;
      case NonPersistedModalName.InfoCasinoComponent:
        return nonPersistedModals.InfoCasinoComponent;
      case NonPersistedModalName.OfferModalComponent:
        return nonPersistedModals.OfferModalComponent;
      case NonPersistedModalName.AdyenModalComponent:
        return nonPersistedModals.AdyenModalComponent;
      case NonPersistedModalName.TermsComponent:
        return nonPersistedModals.TermsComponent;
      case NonPersistedModalName.PrivacyComponent:
        return nonPersistedModals.PrivacyComponent;
      case NonPersistedModalName.CookiesComponent:
        return nonPersistedModals.CookiesComponent;
      case NonPersistedModalName.HighscoreInfoModalComponent:
        return nonPersistedModals.HighscoreInfoModalComponent;
      case NonPersistedModalName.UpdateVersionModalComponent:
        return nonPersistedModals.UpdateVersionModalComponent;
      case NonPersistedModalName.AccountDeletionReactivate:
        return nonPersistedModals.AccountDeletionReactivate;
      case NonPersistedModalName.AccountDeletionStep1:
        return nonPersistedModals.AccountDeletionStep1;
      case NonPersistedModalName.AccountDeletionStep2:
        return nonPersistedModals.AccountDeletionStep2;
      case NonPersistedModalName.AccountDeletionStep3:
        return nonPersistedModals.AccountDeletionStep3;
      case NonPersistedModalName.RateModalComponent:
        return nonPersistedModals.RateModalComponent;
      case NonPersistedModalName.RankingModal:
        return nonPersistedModals.RankingModalComponent;
      case NonPersistedModalName.RankingInfo:
        return nonPersistedModals.RankingInfoComponent;
      case NonPersistedModalName.SlotcardModalComponent:
        return nonPersistedModals.SlotcardModalComponent;
      case NonPersistedModalName.SlotcardInfoModalComponent:
        return nonPersistedModals.SlotcardInfoModalComponent;
      case NonPersistedModalName.MagicBagModalComponent:
        return nonPersistedModals.MagicBagModalComponent;
      case NonPersistedModalName.MagicBagInfoModalComponent:
        return nonPersistedModals.MagicBagInfoModalComponent;
      case NonPersistedModalName.RandomPrizeModalComponent:
        return nonPersistedModals.RandomPrizeModalComponent;
      case NonPersistedModalName.RandomPrizeInfoModalComponent:
        return nonPersistedModals.RandomPrizeInfoModalComponent;
      case NonPersistedModalName.RandomPrizeMinigameModalComponent:
        return nonPersistedModals.RandomPrizeMinigameModalComponent;
      case NonPersistedModalName.ProgressbagModalComponent:
        return nonPersistedModals.ProgressbagModalComponent;
      case NonPersistedModalName.ProgressbagInfoModalComponent:
        return nonPersistedModals.ProgressbagInfoModalComponent;
      case NonPersistedModalName.ProgressbagInfo2ModalComponent:
        return nonPersistedModals.ProgressbagInfo2ModalComponent;
      case NonPersistedModalName.MultieventsModalComponent:
        return nonPersistedModals.MultieventsModalComponent;
      case NonPersistedModalName.GameChallengesModalComponent:
        return nonPersistedModals.GameChallengesModalComponent;
      case NonPersistedModalName.MultiChallengesModalComponent:
        return nonPersistedModals.MultiGameChallengesModalComponent;
      case NonPersistedModalName.TesterModalComponent:
        return nonPersistedModals.TesterModalComponent;
      case NonPersistedModalName.MenuModalComponent:
        return nonPersistedModals.MenuModalComponent;
      case NonPersistedModalName.AdventureQuestModalComponent:
        return nonPersistedModals.AdventureQuestModalComponent;
      case NonPersistedModalName.AdventureQuestInfoModalComponent:
        return nonPersistedModals.AdventureQuestInfoModalComponent;
      case NonPersistedModalName.DailyChallengeModalComponent:
        return nonPersistedModals.DailyChallengeModalComponent;
      case NonPersistedModalName.NotEnoughModalComponent:
        return nonPersistedModals.NotEnoughModalComponent;
      case NonPersistedModalName.InfoDailyChallengeComponent:
        return nonPersistedModals.InfoDailyChallengeComponent;
      case NonPersistedModalName.CompleteWithGemsComponent:
        return nonPersistedModals.CompleteWithGemsComponent;
      case NonPersistedModalName.StreakDetailModalComponent:
        return nonPersistedModals.StreakDetailModalComponent;
      case NonPersistedModalName.KeepStreakComponent:
        return nonPersistedModals.KeepStreakComponent;
      case NonPersistedModalName.RichClubModalComponent:
        return nonPersistedModals.RichClubModalComponent;
      case NonPersistedModalName.PremiumPassModalComponent:
        return nonPersistedModals.PremiumPassModalComponent;
      case NonPersistedModalName.PremiumPassInfoModalComponent:
        return nonPersistedModals.PremiumPassInfoModalComponent;
      case NonPersistedModalName.TournamentComponent:
        return nonPersistedModals.TournamentsComponent;
      case NonPersistedModalName.TournamentInfoComponent:
        return nonPersistedModals.TournamentInfoComponent;
      case NonPersistedModalName.TournamentRankingComponent:
        return nonPersistedModals.TournamentRankingComponent;
      case NonPersistedModalName.OnboardingQuestEndModalComponent:
        return nonPersistedModals.OnboardingQuestEndModalComponent;
      case NonPersistedModalName.ProgressLineCasinoComponent:
        return nonPersistedModals.ProgressLineCasinoComponent;
      case NonPersistedModalName.UnlockFeatureModalComponent:
        return nonPersistedModals.UnlockFeatureModalComponent;
      case NonPersistedModalName.UnlockCasinoComponent:
        return nonPersistedModals.UnlockCasinoComponent;
      case NonPersistedModalName.CasinoMapComponent:
        return nonPersistedModals.CasinoMapComponent;
      case NonPersistedModalName.MultiplierPrizeModalComponent:
        return nonPersistedModals.MultiplierPrizeModalComponent;
      case NonPersistedModalName.WelcomeRewardModalComponent:
        return nonPersistedModals.WelcomeRewardModalComponent;
      default:
        break;
    }
  }

  getPriority(modalType: PersistedModalName): Priority {
    switch (modalType) {
      case PersistedModalName.UpdateTos:
      case PersistedModalName.BuySuccess:
        return Priority.Max;

      case PersistedModalName.Welcome:
      case PersistedModalName.LimitedTimeEventProgress:
        return Priority.Ultra;

      case PersistedModalName.Daily:
      case PersistedModalName.PromoCode:
      case PersistedModalName.PromoCodeExpired:
      case PersistedModalName.PromoCodeRedeemed:
      case PersistedModalName.VideoAds:
      case PersistedModalName.RichClubBonus:
      case PersistedModalName.LimitedTimeEventRedeem:
        return Priority.Highest;

      case PersistedModalName.Campaign:
      case PersistedModalName.CampaignPersisted:
      case PersistedModalName.Bonus:
        return Priority.Higher;

        return Priority.High;

      case PersistedModalName.RestoreStreak:
      case PersistedModalName.RichClubAvailable:
      case PersistedModalName.LimitedTimeEventStart:
        return Priority.Normal;

      case PersistedModalName.NotifyDiscontinued:
      case PersistedModalName.DiscontinuedReward:
      case PersistedModalName.StartNow:
        return Priority.Low;

      case PersistedModalName.OfferPlaceholder:
      case PersistedModalName.RollingBonus:
      case PersistedModalName.Default:
        return Priority.Lowest;
    }
  }

  private manageLimitedTimeEventStart(modal: ModalConfig): ComponentType<unknown> {
    switch (modal.properties.eventType) {
      case LimitedTimeEventType.Slotcard: {
        return persistedModals.SlotcardStartModalComponent;
      }

      case LimitedTimeEventType.Magicbag: {
        return persistedModals.MagicBagModalComponent;
      }

      case LimitedTimeEventType.MultiplierPrize: {
        return persistedModals.MultiplierPrizeModalComponent;
      }

      case LimitedTimeEventType.RandomPrize: {
        return persistedModals.RandomPrizeStartModalComponent;
      }

      case LimitedTimeEventType.ProgressiveBag: {
        return persistedModals.ProgressbagStartModalComponent;
      }

      case LimitedTimeEventType.AdventureQuest: {
        return persistedModals.AdventureQuestStartModalComponent;
      }

      case LimitedTimeEventType.PremiumPass: {
        return persistedModals.PremiumPassStartModalComponent;
      }

      case LimitedTimeEventType.Tournament: {
        return persistedModals.TournamentStartComponent;
      }

      case LimitedTimeEventType.GameChallenges: {
        switch (modal?.properties?.challengeEventType) {
          case ChallengeEventType.Individual:
            return persistedModals.GameChallengesModalComponent;
          case ChallengeEventType.Multi:
            return persistedModals.MultiChallengesStartModalComponent;

          default:
            break;
        }
      }
    }
  }

  private manageLimitedTimeEventProgress(modal: ModalConfig): ComponentType<unknown> {
    switch (modal.properties.eventType) {
      case LimitedTimeEventType.Slotcard: {
        return persistedModals.SlotcardProgressModalComponent;
      }

      case LimitedTimeEventType.RandomPrize: {
        return persistedModals.RandomPrizeProgressModalComponent;
      }

      case LimitedTimeEventType.ProgressiveBag: {
        return persistedModals.ProgressbagProgressModalComponent;
      }

      default:
        break;
    }
  }

  private manageLimitedTimeEventReward(modal: ModalConfig): ComponentType<unknown> {
    switch (modal.properties.eventType) {
      case LimitedTimeEventType.Slotcard: {
        return persistedModals.SlotcardRewardModalComponent;
      }

      case LimitedTimeEventType.Magicbag: {
        return persistedModals.MagicBagRewardModalComponent;
      }

      case LimitedTimeEventType.RandomPrize: {
        return persistedModals.RandomPrizeRewardModalComponent;
      }

      case LimitedTimeEventType.ProgressiveBag: {
        return persistedModals.ProgressbagRewardModalComponent;
      }

      case LimitedTimeEventType.GameChallenges: {
        return persistedModals.MultiChallengesRewardModalComponent;
      }

      case LimitedTimeEventType.DailyChallenge: {
        return persistedModals.PendingRewardsModalComponent;
      }

      case LimitedTimeEventType.AdventureQuest: {
        return persistedModals.MultiChallengesRewardModalComponent;
      }

      case LimitedTimeEventType.PremiumPass: {
        return persistedModals.PremiumPassRewardModalComponent;
      }

      case LimitedTimeEventType.Tournament: {
        return persistedModals.TournamentRewardComponent;
      }

      case LimitedTimeEventType.OnboardingQuest: {
        return persistedModals.OnboardingQuestRewardModalComponent;
      }

      default:
        break;
    }
  }
}
